import {useContext, useEffect, useState} from "react";
import {LAKE_CHART_MODES} from "../utils/ChartFormatUtils";
import SensorService from "../API/SensorService";
import {
    getDateByTimestamp,
    getMinusMonthTimestamp,
    getMinusWeekTimestamp,
    getMinusYearTimestamp,
    getEachFifthDayNumber,
    getTodayEndTimestamp,
    getWeekDayName,
    INTERVALS,
    getFormattedDayNumberWithYear,
    FIRST_TIMESTAMP,
    getFormattedTooltipTimeAndDate,
    formatTimeWithDayNumberAndMonthName, getEndOfDay
} from "../utils/DateUtils";
import {AuthContext} from "../context";

export function useChartData() {
    const {isAuth} = useContext(AuthContext)
    const [activeMode, setActiveMode] = useState(LAKE_CHART_MODES.week)

    const [weekData, setWeekData] = useState() //labels, temperatures, waterLevels
    const [monthData, setMonthData] = useState()
    const [yearData, setYearData] = useState()
    const [maxData, setMaxData] = useState()

    const todayTimestamp = getTodayEndTimestamp()

    useEffect(() => {
        if (isAuth) {
            fetchData()
        }
    }, [activeMode, isAuth])

    function fetchData() {
        if (!weekData) {
            fetchWeekData()
        }
        if (!monthData) {
            fetchMonthData()
        }
        if (!yearData) {
            fetchYearData()
        }
        if (!maxData) {
            fetchMaxData()
        }
    }

    function labelObjectWithAxisValue(axisValue) {
        return JSON.stringify({
            axisValue: axisValue,
            tooltipValue: ''
        })
    }

    function filterResponse(response) {
        return response.data.filter(element => element.value !== 0)
    }

    function fetchWeekData() {
        const minusWeekTimestamp = getMinusWeekTimestamp()
        SensorService.fetchTemperature(minusWeekTimestamp, todayTimestamp, INTERVALS.halfDay)
            .then(response=> {
                const filteredData = filterResponse(response)
                filteredData.shift() //removing first element, so first day of chart didn't have 3 values as server returns

                const labels = []
                filteredData.forEach(element => {
                    labels.push(JSON.stringify({
                        axisValue: labels.length % 2 === 0
                            ? getWeekDayName(element.timestamp)
                            : '',
                        tooltipValue: formatTimeWithDayNumberAndMonthName(getDateByTimestamp(element.timestamp))
                    }))
                })
                const temperatures = filteredData.map(element => element.value)

                setWeekData(prev => ({
                    ...prev,
                    labels: labels,
                    temperatures: temperatures
                }))
            })
        SensorService.fetchWaterLevel(minusWeekTimestamp, todayTimestamp, INTERVALS.halfDay)
            .then(response => {
                const filteredData = filterResponse(response)
                filteredData.shift()

                setWeekData(prev => ({
                    ...prev,
                    waterLevels: filteredData.map(element => element.value)
                }))
            })
    }

    function fetchMonthData() {
        const minusMonthTimestamp = getMinusMonthTimestamp()
        SensorService.fetchTemperature(minusMonthTimestamp, todayTimestamp, INTERVALS.day)
            .then(response=> {
                const filteredData = filterResponse(response)

                const labels = filteredData.map(element => JSON.stringify({
                    axisValue: getEachFifthDayNumber(getDateByTimestamp(element.timestamp)),
                    tooltipValue: formatTimeWithDayNumberAndMonthName(getDateByTimestamp(element.timestamp))
                }))
                const temperatures = filteredData.map(element => element.value)

                const earliestDate = response.data.length > 0
                    ? getDateByTimestamp(filteredData[0].timestamp)
                    : getEndOfDay()
                for (let i = 0; i < 31 - filteredData.length; i++) {
                    earliestDate.setDate(earliestDate.getDate() - 1)
                    labels.unshift(labelObjectWithAxisValue(getEachFifthDayNumber(earliestDate)))
                    temperatures.unshift(null)
                }

                setMonthData(prev => ({
                    ...prev,
                    labels: labels,
                    temperatures: temperatures
                }))
            })
        SensorService.fetchWaterLevel(minusMonthTimestamp, todayTimestamp, INTERVALS.day)
            .then(response => {
                const filteredData = filterResponse(response)

                const waterLevels = filteredData.map(element => element.value)
                for (let i = 0; i < 31 - filteredData.length; i++) {
                    waterLevels.unshift(null)
                }

                setMonthData(prev => ({
                    ...prev,
                    waterLevels: waterLevels
                }))
            })
    }

    function fetchYearData() {
        const minusYearTimestamp = getMinusYearTimestamp()
        SensorService.fetchTemperature(minusYearTimestamp, todayTimestamp, INTERVALS.halfWeek)
            .then(response=> {
                const filteredData = filterResponse(response)
                const labels = filteredData.map(element => JSON.stringify({
                    axisValue: getFormattedDayNumberWithYear(element.timestamp),
                    tooltipValue: formatTimeWithDayNumberAndMonthName(getDateByTimestamp(element.timestamp))
                }))
                const temperatures = filteredData.map(element => element.value)

                const earliestDate = filteredData.length > 0
                    ? getDateByTimestamp(filteredData[0].timestamp)
                    : getEndOfDay()
                for (let i = 0; i < 104 - filteredData.length; i++) {
                    const daysToMinusLabel = 3 + (i % 2)
                    earliestDate.setDate(earliestDate.getDate() - daysToMinusLabel)
                    labels.unshift(labelObjectWithAxisValue(getFormattedTooltipTimeAndDate(earliestDate)))

                    temperatures.unshift(null)
                }
                setYearData(prev => ({
                    ...prev,
                    labels: labels,
                    temperatures: temperatures
                }))
            })
        SensorService.fetchWaterLevel(minusYearTimestamp, todayTimestamp, INTERVALS.halfWeek)
            .then(response => {
                const filteredData = filterResponse(response)
                const waterLevels = filteredData.map(element => element.value)
                for (let i = 0; i < 104 - filteredData.length; i++) {
                    waterLevels.unshift(null)
                }

                setYearData(prev => ({
                    ...prev,
                    waterLevels: waterLevels
                }))
            })
    }

    function fetchMaxData() {
        SensorService.fetchTemperature(FIRST_TIMESTAMP, todayTimestamp, INTERVALS.week)
            .then(response=> {
                const filteredData = filterResponse(response)
                const labels = filteredData.map(element => JSON.stringify({
                    axisValue: getFormattedDayNumberWithYear(element.timestamp),
                    tooltipValue: formatTimeWithDayNumberAndMonthName(getDateByTimestamp(element.timestamp))
                }))
                const temperatures = filteredData.map(element => element.value)

                setMaxData(prev => ({
                    ...prev,
                    labels: labels,
                    temperatures: temperatures
                }))
            })
        SensorService.fetchWaterLevel(FIRST_TIMESTAMP, todayTimestamp, INTERVALS.week)
            .then(response => {
                const filteredData = filterResponse(response)
                const waterLevels = filteredData.map(element => element.value)

                setMaxData(prev => ({
                    ...prev,
                    waterLevels: waterLevels
                }))
            })
    }

    let activeData
    let activeModeStartTimestamp
    switch (activeMode) {
        case LAKE_CHART_MODES.week:
            activeData = weekData
            activeModeStartTimestamp = getMinusWeekTimestamp()
            break
        case LAKE_CHART_MODES.month:
            activeData = monthData
            activeModeStartTimestamp = getMinusMonthTimestamp()
            break
        case LAKE_CHART_MODES.year:
            activeData = yearData
            activeModeStartTimestamp = getMinusYearTimestamp()
            break
        case LAKE_CHART_MODES.max:
            activeData = maxData
            activeModeStartTimestamp = FIRST_TIMESTAMP
            break
        default:
            activeData = weekData
            activeModeStartTimestamp = getMinusWeekTimestamp()
            break
    }

    function downloadXlsxData() {
        SensorService.downloadXlsxData(activeModeStartTimestamp, todayTimestamp);
    }

    return [activeMode, setActiveMode, activeData, downloadXlsxData]
}